import React, { useMemo } from "react";
import PropTypes from "prop-types";

// local component imports
import BentoGrid from "./BentoGrid";
import NormalGrid from "./NormalGrid";
import ThreeGridBento from "./ThreeGridBento";

// services and utils imports
import { useVideoPlayer } from "../../../../../customHooks/useVideoPlayer";

/**
 * DynamicGrid renders either a BentoGrid or NormalGrid based on layoutSnapshot configuration.
 * @param {object} props - Component props.
 * @param {object} props.layoutSnapshot - Layout config with gridConfig and type.
 * @param {Array} props.content - Content items to merge into cells.
 * @param {object} props.sectionSettings - Section-level settings including gridTextStyle and gridBorder.
 * @param {boolean} props.isAboveFold - Whether the grid is expected above the fold.
 * @param {boolean} props.isLcpCandidate - Whether the grid may contain the LCP image.
 * @returns {React.ReactElement|null} The grid or null if config is invalid.
 */
const DynamicGrid = ({
  layoutSnapshot,
  content = [],
  sectionSettings,
  isMobile,
  isAboveFold = false,
  isLcpCandidate = false,
}) => {
  const { playingVideos, videoRefs, toggleVideoPlay: onToggleVideoPlay } = useVideoPlayer();

  const gridConfig = layoutSnapshot?.gridConfig;
  const rows = useMemo(() => gridConfig?.rows || [], [gridConfig?.rows]);
  const isBento = gridConfig?.gridType === "bento";
  const gridTextStyle = sectionSettings?.gridTextStyle || {};
  const gridBorder = sectionSettings?.gridBorder || {};

  /**
   * Returns a stable key for a cell object.
   * @param {object} cell - The cell object.
   * @param {string} fallback - Fallback key string.
   * @returns {string} The cell key.
   */
  const getCellKey = (cell, fallback) => cell.cellId || cell.id || fallback;

  const contentMap = useMemo(() => {
    const map = new Map();
    content.forEach((c, i) => {
      const key = getCellKey(c, `content-${i}`);
      map.set(key, c);
    });
    return map;
  }, [content]);

  if (!layoutSnapshot || layoutSnapshot.type !== "grid" || !gridConfig) return null;

  const gridProps = {
    rows,
    gridConfig,
    contentMap,
    playingVideos,
    videoRefs,
    onToggleVideoPlay,
    gridTextStyle,
    gridBorder,
    isMobile,
    isAboveFold,
    isLcpCandidate,
  };

  // Flat ordered list of all cells merged with contentMap — used for ThreeGridBento
  const flatCells = rows.flatMap(
    (row) =>
      row?.cells?.map((cell, cellIndex) => {
        const key = getCellKey(cell, `${row.id}-${cellIndex}`);
        const fromContent = contentMap.get(key);
        return fromContent ? { ...cell, ...fromContent } : cell;
      }) || [],
  );

  if (isBento && flatCells.length === 3) {
    return (
      <ThreeGridBento
        cells={flatCells}
        gridConfig={gridConfig}
        playingVideos={playingVideos}
        videoRefs={videoRefs}
        onToggleVideoPlay={onToggleVideoPlay}
        gridTextStyle={gridTextStyle}
        gridBorder={gridBorder}
        isMobile={isMobile}
        isAboveFold={isAboveFold}
        isLcpCandidate={isLcpCandidate}
      />
    );
  }

  return isBento ? <BentoGrid {...gridProps} /> : <NormalGrid {...gridProps} />;
};

DynamicGrid.propTypes = {
  layoutSnapshot: PropTypes.object,
  content: PropTypes.arrayOf(PropTypes.object),
  sectionSettings: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
};

export default DynamicGrid;
